// ------------------------------
// components/PatientAppointmentsTab.jsx (Patient-facing) - REFACTORED
// ------------------------------
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCurrentUser } from "../APIs/UserContext";
import {
  fetchPatientAppointmentCardsByDate,
  cancelAppointmentPatient,
  ZONES,
} from "../APIs/patientAppointmentApi";

const PATIENT_TABS = [
  { key: "scheduled", label: "Scheduled", states: ["SCHEDULED"] },
  { key: "completed", label: "Completed Appointments", states: ["COMPLETED"] },
  { key: "cancelled", label: "Cancelled Appointments", states: ["CANCELLED"] },
  { key: "noshow", label: "No-Shows", states: ["NOSHOW"] },
  { key: "past", label: "Past Appointments", states: ["SCHEDULED_PAST"] },
];

function todayYYYYMMDDInZone(zone) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone || "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());

    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch {
    // ignore
  }

  // fallback (browser local)
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatInZone(isoLike, zone) {
  try {
    const dt = new Date(isoLike);
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: zone || "America/New_York",
    }).format(dt);
  } catch {
    return isoLike ?? "";
  }
}

export function PatientAppointmentsTab() {
  const { currentUser } = useCurrentUser();
  const patientId = currentUser?.patientId;

  const { search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  // ✅ read query params once on mount / when URL changes
  const initialTab = qs.get("tab");
  const initialDate = qs.get("date"); // "YYYY-MM-DD"
  const initialZone = qs.get("zone"); // optional

  const [activeKey, setActiveKey] = useState(() => {
    const ok = PATIENT_TABS.some((t) => t.key === initialTab);
    return ok ? initialTab : "scheduled";
  });

  const [zone, setZone] = useState(() => initialZone || "America/New_York");

  const [date, setDate] = useState(() => {
    // If caller passed date, use it; else compute today in zone
    return (
      initialDate || todayYYYYMMDDInZone(initialZone || "America/New_York")
    );
  });

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingId, setPendingId] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);

  const handleBackToList = () => {
    setSelectedCard(null);
  };

  // ✅ Keep state in sync if user lands on this route again with different params
  useEffect(() => {
    const ok = PATIENT_TABS.some((t) => t.key === initialTab);
    const nextTab = ok ? initialTab : "scheduled";

    setActiveKey(nextTab);
    setSelectedCard(null);

    const nextZone = initialZone || "America/New_York";
    setZone(nextZone);

    // Only override date if URL provides one; otherwise keep current date state
    if (initialDate) {
      setDate(initialDate);
    } else {
      // if URL changes zone and date wasn't explicitly provided, update date to "today in zone"
      setDate(todayYYYYMMDDInZone(nextZone));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, initialDate, initialZone]);

  const states = useMemo(
    () => PATIENT_TABS.find((t) => t.key === activeKey)?.states || [],
    [activeKey],
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const page = await fetchPatientAppointmentCardsByDate({
          patientId,
          date,
          zone,
          states,
          page: 0,
          size: 100,
        });

        if (!cancelled) setPageData(page);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (patientId) load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, date, zone, states.join(",")]);

  const renderDetailView = () => {
    const isScheduled = activeKey === "scheduled";
    const isPending = pendingId === selectedCard.appointmentId;

    return (
      <div className="max-w-3xl p-4 space-y-4">
        <button
          onClick={handleBackToList}
          className="mb-4 text-sm px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 flex items-center gap-1"
        >
          &larr; Back to Appointments
        </button>

        <h3 className="text-xl font-semibold">Appointment Details</h3>

        <div className="p-4 border rounded bg-white space-y-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <div className="text-gray-500">First name</div>
            <div>{selectedCard.firstName}</div>
            <div className="text-gray-500">Last name</div>
            <div>{selectedCard.lastName}</div>
            <div className="text-gray-500">Email</div>
            <div>{selectedCard.email}</div>
            <div className="text-gray-500">Phone</div>
            <div>{selectedCard.phoneNumber}</div>
            <div className="text-gray-500">Start</div>
            <div>{formatInZone(selectedCard.appointmentStart, zone)}</div>
            <div className="text-gray-500">End</div>
            <div>{formatInZone(selectedCard.appointmentEnd, zone)}</div>
          </div>
        </div>

        {isScheduled && (
          <div className="flex flex-wrap gap-2">
            <button
              className="px-3 py-1.5 rounded-xl text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              disabled={isPending}
              onClick={async () => {
                if (!window.confirm("Cancel this appointment?")) return;

                const reason = window.prompt(
                  "Enter cancellation reason (required)",
                );
                if (!reason || !reason.trim()) {
                  alert("Cancellation reason is required");
                  return;
                }

                try {
                  setPendingId(selectedCard.appointmentId);
                  await cancelAppointmentPatient({
                    appointmentId: selectedCard.appointmentId,
                    patientId,
                    reason: reason.trim(),
                  });
                  setSelectedCard(null);
                  setActiveKey("cancelled");
                } catch (err) {
                  alert(err?.message || "Failed to cancel");
                } finally {
                  setPendingId(null);
                }
              }}
            >
              Cancel Appointment
            </button>

            <a
              href={`mailto:${selectedCard.email}`}
              className="px-3 py-1.5 rounded-xl text-sm bg-blue-600 text-white hover:bg-blue-700"
            >
              Contact Provider
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence mode="wait">
      {selectedCard ? (
        <motion.div
          key="detail"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.3 }}
        >
          {renderDetailView()}
        </motion.div>
      ) : (
        <motion.div
          key="list"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 30 }}
          transition={{ duration: 0.3 }}
        >
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">My Appointments</h3>

            {/* Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block mb-1 font-medium">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block mb-1 font-medium">Time Zone</label>
                <select
                  value={zone}
                  onChange={(e) => {
                    const z = e.target.value;
                    setZone(z);
                    // ✅ update date to today's date in the newly selected zone
                    setDate(todayYYYYMMDDInZone(z));
                  }}
                  className="w-full border rounded px-3 py-2"
                >
                  {ZONES.map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2">
              {PATIENT_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveKey(tab.key)}
                  className={`px-3 py-2 rounded-2xl text-sm whitespace-nowrap ${
                    activeKey === tab.key
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Results */}
            <div className="min-h-[120px]">
              {loading && <div className="text-gray-600">Loading…</div>}
              {error && !loading && <div className="text-red-600">{error}</div>}

              {!loading && !error && (
                <div className="grid gap-3">
                  {(pageData?.content || []).length === 0 ? (
                    <div className="text-gray-500">
                      No appointments found for this selection.
                    </div>
                  ) : (
                    pageData.content.map((card) => {
                      const isScheduled = activeKey === "scheduled";
                      const isPending = pendingId === card.appointmentId;

                      return (
                        <div
                          key={card.appointmentId}
                          className="w-full bg-white border rounded-2xl shadow-sm p-4 cursor-pointer"
                          onClick={() => setSelectedCard(card)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-lg font-semibold">
                                {card.firstName} {card.lastName}
                              </div>
                              <div className="text-sm text-gray-600">
                                {card.email}
                              </div>
                              <div className="text-sm text-gray-600">
                                {card.phoneNumber}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium">Start</div>
                              <div className="text-sm">
                                {formatInZone(card.appointmentStart, zone)}
                              </div>
                              <div className="text-sm font-medium mt-1">
                                End
                              </div>
                              <div className="text-sm">
                                {formatInZone(card.appointmentEnd, zone)}
                              </div>
                            </div>
                          </div>

                          {isScheduled && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                className="px-3 py-1.5 rounded-xl text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                disabled={isPending}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (
                                    !window.confirm("Cancel this appointment?")
                                  )
                                    return;

                                  const reason = window.prompt(
                                    "Enter cancellation reason (required)",
                                  );
                                  if (!reason || !reason.trim()) {
                                    alert("Cancellation reason is required");
                                    return;
                                  }

                                  try {
                                    setPendingId(card.appointmentId);
                                    await cancelAppointmentPatient({
                                      appointmentId: card.appointmentId,
                                      patientId,
                                      reason: reason.trim(),
                                    });
                                    setActiveKey("cancelled");
                                  } catch (err) {
                                    alert(err?.message || "Failed to cancel");
                                  } finally {
                                    setPendingId(null);
                                  }
                                }}
                              >
                                Cancel Appointment
                              </button>

                              <a
                                href={`mailto:${card.email}`}
                                className="px-3 py-1.5 rounded-xl text-sm bg-blue-600 text-white hover:bg-blue-700"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Contact Provider
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
